/**
 * Orchestra AI Platform - Search Bar Component
 * 
 * Search bar with filters and search history
 * 
 * @fileoverview Search bar component for API toolbox
 */

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './SearchBar.css';

/**
 * @typedef {Object} SearchBarProps
 * @property {string} value - Search query value
 * @property {Function} onChange - Change handler
 * @property {Function} [onClear] - Clear handler
 */

/**
 * Search Bar Component
 * @param {SearchBarProps} props
 */
export function SearchBar({ value, onChange, onClear }) {
  const [searchHistory, setSearchHistory] = useState([]);

  // Load search history from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('api_search_history');
    if (saved) {
      try {
        setSearchHistory(JSON.parse(saved));
      } catch (error) {
        console.error('Failed to load search history:', error);
      }
    }
  }, []);

  /**
   * Handle input change
   * @param {React.ChangeEvent<HTMLInputElement>} event
   */
  const handleChange = (event) => {
    const newValue = event.target.value;
    onChange(newValue);
  };

  /**
   * Handle clear
   */
  const handleClear = () => {
    onChange('');
    if (onClear) {
      onClear();
    }
  };

  /**
   * Handle key down
   * @param {React.KeyboardEvent<HTMLInputElement>} event
   */
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleClear();
    }
  };

  /**
   * Save to search history
   * @param {string} query
   */
  const saveToHistory = (query) => {
    if (!query || query.trim().length === 0) return;

    const updated = [query, ...searchHistory.filter((h) => h !== query)].slice(0, 10);
    setSearchHistory(updated);
    localStorage.setItem('api_search_history', JSON.stringify(updated));
  };

  // Save to history when value changes (debounced)
  useEffect(() => {
    if (value && value.trim().length > 0) {
      const timer = setTimeout(() => {
        saveToHistory(value);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [value]);

  return (
    <div className="search-bar">
      <div className="search-bar-input-wrapper">
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Search APIs by name, description, tags..."
          className="search-bar-input"
        />
        <div className="search-bar-icon">
          🔍
        </div>
        {value && (
          <button
            onClick={handleClear}
            className="search-bar-clear"
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {/* Search suggestions (if needed) */}
      {value && searchHistory.length > 0 && (
        <div className="search-bar-suggestions">
          {searchHistory
            .filter((h) => h.toLowerCase().includes(value.toLowerCase()))
            .slice(0, 5)
            .map((historyItem, index) => (
              <button
                key={index}
                onClick={() => onChange(historyItem)}
                className="search-bar-suggestion-item"
              >
                {historyItem}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

SearchBar.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func,
};

